import React from 'react';

import { DeleteOutlined } from '@ant-design/icons';
import { Button, Col, Flex, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { TableControlsContainer } from '@components/TableControlsContainer';

import { WarehouseDocumentKind } from '../../interfaces';

import {
  RecipientDocumentFilterSelect,
  SenderDocumentFilterSelect,
  StatusDocumentFilterSelect,
} from './components/DocumentFilterSelect';
import { FilterEditMenu } from './components/FilterEditMenu';
import { useWarehouseDocumentSearchParams } from './hooks';
import { Filters } from './interfaces';

interface IProps {
  disabled?: boolean;
  kind: WarehouseDocumentKind;
  warehouseUid: string;
}

export const SearchControls: React.FC<IProps> = ({ disabled, kind, warehouseUid }) => {
  const commonT = useTranslation(NSKeys.common);
  const documentsT = useTranslation(NSKeys.warehouseDocuments);
  const {
    searchInputValue,
    recipientValue,
    senderValue,
    statusValue,
    handleRecipientChange,
    handleReset,
    handleSearch,
    handleSenderChange,
    handleStatusChange,
    setSearchInputValue,
  } = useWarehouseDocumentSearchParams();
  const [selectedFilters, setSelectedFilters] = React.useState<Filters[]>(() => [
    ...(statusValue.length ? [Filters.STATUS] : []),
    ...(kind === 'receipt' && senderValue.length ? [Filters.SENDER] : []),
    ...(kind === 'shipment' && recipientValue.length ? [Filters.RECIPIENT] : []),
  ]);
  const filters = React.useMemo(
    () =>
      kind === 'receipt'
        ? [
            { value: Filters.STATUS, label: documentsT.t('filters.status') },
            { value: Filters.SENDER, label: documentsT.t('filters.sender') },
          ]
        : [
            { value: Filters.STATUS, label: documentsT.t('filters.status') },
            { value: Filters.RECIPIENT, label: documentsT.t('filters.recipient') },
          ],
    [documentsT, kind]
  );

  React.useEffect(() => {
    setSelectedFilters((prev) => {
      const nextFilters = [...prev];

      if (statusValue.length && !nextFilters.includes(Filters.STATUS)) {
        nextFilters.push(Filters.STATUS);
      }

      if (kind === 'receipt' && senderValue.length && !nextFilters.includes(Filters.SENDER)) {
        nextFilters.push(Filters.SENDER);
      }

      if (kind === 'shipment' && recipientValue.length && !nextFilters.includes(Filters.RECIPIENT)) {
        nextFilters.push(Filters.RECIPIENT);
      }

      return nextFilters.filter((filter) =>
        kind === 'receipt'
          ? filter === Filters.STATUS || filter === Filters.SENDER
          : filter === Filters.STATUS || filter === Filters.RECIPIENT
      );
    });
  }, [kind, recipientValue.length, senderValue.length, statusValue.length]);

  const handleFilterMenuChange = (filters: Filters[]) => {
    if (!filters.includes(Filters.STATUS)) {
      handleStatusChange([]);
    }

    if (!filters.includes(Filters.RECIPIENT)) {
      handleRecipientChange([]);
    }

    if (!filters.includes(Filters.SENDER)) {
      handleSenderChange([]);
    }

    setSelectedFilters(filters);
  };

  const handleResetClick = () => {
    setSelectedFilters([]);
    handleReset();
  };

  return (
    <TableControlsContainer
      showBadge={
        !!searchInputValue ||
        !!statusValue.length ||
        (kind === 'receipt' && !!senderValue.length) ||
        (kind === 'shipment' && !!recipientValue.length)
      }>
      <React.Fragment>
        <Flex justify="end" wrap="wrap" gap={16} style={{ marginBottom: 16 }}>
          <FilterEditMenu filters={filters} selectedFilters={selectedFilters} onChange={handleFilterMenuChange} />

          <Form.Item style={{ marginBottom: 0 }}>
            <Input.Search
              style={{ width: 280 }}
              size="middle"
              value={searchInputValue}
              onChange={(e) => setSearchInputValue(e.target.value)}
              placeholder={commonT.t('search')}
              enterButton
              allowClear
              disabled={disabled}
              onSearch={handleSearch}
            />
          </Form.Item>
        </Flex>

        {!!selectedFilters.length && (
          <Flex gap={16} wrap="wrap">
            {selectedFilters.map((filter) => (
              <Col style={{ width: 250 }} key={`filter-${filter}`}>
                {filter === Filters.STATUS && (
                  <StatusDocumentFilterSelect
                    disabled={disabled}
                    kind={kind}
                    placeholder={documentsT.t('filters.status')}
                    value={statusValue}
                    onChange={handleStatusChange}
                    warehouseUid={warehouseUid}
                  />
                )}
                {filter === Filters.RECIPIENT && (
                  <RecipientDocumentFilterSelect
                    disabled={disabled}
                    kind={kind}
                    placeholder={documentsT.t('filters.recipient')}
                    value={recipientValue}
                    onChange={handleRecipientChange}
                    warehouseUid={warehouseUid}
                  />
                )}
                {filter === Filters.SENDER && (
                  <SenderDocumentFilterSelect
                    disabled={disabled}
                    kind={kind}
                    placeholder={documentsT.t('filters.sender')}
                    value={senderValue}
                    onChange={handleSenderChange}
                    warehouseUid={warehouseUid}
                  />
                )}
              </Col>
            ))}

            <Form.Item style={{ marginBottom: 10 }}>
              <Button type="link" icon={<DeleteOutlined />} onClick={handleResetClick}>
                {commonT.t('buttonReset')}
              </Button>
            </Form.Item>
          </Flex>
        )}
      </React.Fragment>
    </TableControlsContainer>
  );
};
